import * as React from "react";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { SectionHeader } from "../../../../../Components/Clients/NewBusinessWizard/Components/SharedComponents";
import {
    editIncomeDrawdownDetailsLabels,
    editIncomeDrawdownDetailsMessages,
    TransferOrDrawdown,
} from "../../Resource/Contents";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { processTypeEnum } from "../../../Common/Resource/Contents";
import {
    calculateMaximumIncomePaymentForFutureYear,
    calculateMaximumIncomePaymentForRemainderOfYear,
    maximumIncomePerPaymentForFlexiTransfers,
} from "../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";

export type CappedTransferRowData = {
    CappedTransfer1: TransferOrDrawdown;
    CappedTransfer2?: TransferOrDrawdown;
};

interface ICappedTransferRowProps extends IProcessComponentProps {
    data: CappedTransferRowData;
    onChange: (changes: any, transfer: TransferOrDrawdown) => void;
    pensionBase: PensionBase;
}
interface ICappedTransferRowState {}

export class CappedTransferRow extends ProcessComponent<ICappedTransferRowProps, ICappedTransferRowState> {
    constructor(props: any) {
        super(props);
    }

    public render(): JSX.Element {
        const { data, onChange, pensionBase } = this.props;

        return (
            <>
                {SectionHeader(editIncomeDrawdownDetailsLabels.capped)}
                <div className="row">
                    <span className="col-4 h4">{editIncomeDrawdownDetailsLabels.from}</span>
                    <span className="col-4" data-test="from1">
                        {data.CappedTransfer1.ProviderDetails?.Name}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="from2">
                            {data.CappedTransfer2?.ProviderDetails?.Name}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{editIncomeDrawdownDetailsLabels.amount}</span>
                    <span className="col-4" data-test="amount1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            maximumIncomePerPaymentForFlexiTransfers([data.CappedTransfer1], true),
                            data.CappedTransfer1.CurrentValue?.Currency || CurrencyCode.GBP
                        )}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="amount2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                maximumIncomePerPaymentForFlexiTransfers([data.CappedTransfer2], true),
                                data.CappedTransfer2?.CurrentValue?.Currency || CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{editIncomeDrawdownDetailsLabels.type}</span>
                    <span className="col-4" data-test="type1">
                        {data?.CappedTransfer1.IsInheritedFund
                            ? editIncomeDrawdownDetailsLabels.inherited
                            : editIncomeDrawdownDetailsLabels.member}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="type2">
                            {data?.CappedTransfer2?.IsInheritedFund
                                ? editIncomeDrawdownDetailsLabels.inherited
                                : editIncomeDrawdownDetailsLabels.member}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{editIncomeDrawdownDetailsLabels.gadMaximum}</span>
                    <span className="col-4" data-test="gadMaximum1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            data.CappedTransfer1.IncomePaymentDetails?.GadMaximum?.Amount,
                            data.CappedTransfer1.IncomePaymentDetails?.GadMaximum?.Currency || CurrencyCode.GBP
                        )}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="gadMaximum2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                data.CappedTransfer2?.IncomePaymentDetails?.GadMaximum?.Amount,
                                data.CappedTransfer2?.IncomePaymentDetails?.GadMaximum?.Currency || CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{editIncomeDrawdownDetailsLabels.incomeTakenToDate}</span>

                    <span className="col-4" data-test="incomeTakenToDate1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            data?.CappedTransfer1.IncomePaymentDetails?.CurrentYearIncome?.Amount,
                            CurrencyCode.GBP
                        )}
                    </span>

                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="incomeTakenToDate2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                data?.CappedTransfer2?.IncomePaymentDetails?.CurrentYearIncome?.Amount,
                                CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{editIncomeDrawdownDetailsLabels.nextYearsGadMaximum}</span>
                    <span className="col-4" data-test="nextYearsGadMaximum1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            data.CappedTransfer1.IncomePaymentDetails?.GadMaximum?.Amount,
                            data.CappedTransfer1.IncomePaymentDetails?.GadMaximum?.Currency || CurrencyCode.GBP
                        )}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="nextYearsGadMaximum2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                data.CappedTransfer2?.IncomePaymentDetails?.GadMaximum?.Amount,
                                data.CappedTransfer2?.IncomePaymentDetails?.GadMaximum?.Currency || CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>

                <div className="row">
                    <span className="col-4 h4">{editIncomeDrawdownDetailsLabels.lastMandatoryReview}</span>
                    <span className="col-4" data-test="lastMandatoryReview1">
                        {data.CappedTransfer1.IncomePaymentDetails?.LastGadReviewDate &&
                            JsonDateParser.getFormatDateString(
                                JsonDateParser.toDate(data.CappedTransfer1.IncomePaymentDetails?.LastGadReviewDate),
                                "dd/MM/yyyy"
                            )}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="lastMandatoryReview2">
                            {data.CappedTransfer2?.IncomePaymentDetails?.LastGadReviewDate &&
                                JsonDateParser.getFormatDateString(
                                    JsonDateParser.toDate(
                                        data.CappedTransfer2?.IncomePaymentDetails?.LastGadReviewDate
                                    ),
                                    "dd/MM/yyyy"
                                )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">
                        {editIncomeDrawdownDetailsLabels.maximumIncomePaymentForRemainderOfYear}
                    </span>
                    <span className="col-4" data-test="maximumIncomePaymentForRemainderOfYear1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            calculateMaximumIncomePaymentForRemainderOfYear(
                                processTypeEnum.NewBusiness,
                                data?.CappedTransfer1,
                                pensionBase
                            ),
                            CurrencyCode.GBP
                        )}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="maximumIncomePaymentForRemainderOfYear2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                calculateMaximumIncomePaymentForRemainderOfYear(
                                    processTypeEnum.NewBusiness,
                                    data?.CappedTransfer2,
                                    pensionBase
                                ),
                                CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">
                        {editIncomeDrawdownDetailsLabels.maximumIncomePaymentForFutureYears}
                    </span>
                    <span className="col-4" data-test="maximumIncomePaymentForFutureYears1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            calculateMaximumIncomePaymentForFutureYear(
                                processTypeEnum.NewBusiness,
                                data?.CappedTransfer1,
                                pensionBase
                            ),
                            CurrencyCode.GBP
                        )}
                    </span>
                    {data.CappedTransfer2 && (
                        <span className="col-4" data-test="maximumIncomePaymentForFutureYears2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                calculateMaximumIncomePaymentForFutureYear(
                                    processTypeEnum.NewBusiness,
                                    data?.CappedTransfer2,
                                    pensionBase
                                ),
                                CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">
                        {editIncomeDrawdownDetailsLabels.incomePerPaymentForCurrentPensionYear}
                    </span>
                    <div className="col-4">
                        <MoneyTextBoxEditor
                            data-test="currentIncome1"
                            label=""
                            value={data?.CappedTransfer1.IncomePaymentDetails?.GrossIncomePerPayment?.Amount}
                            currency={CurrencyCode.GBP}
                            onChange={(v) =>
                                onChange(
                                    {
                                        GrossIncomePerPayment: {
                                            Amount: v,
                                            Currency: CurrencyCode.GBP,
                                        },
                                    },
                                    data.CappedTransfer1
                                )
                            }
                            required={editIncomeDrawdownDetailsMessages.incomePerPaymentForCurrentPensionYearRequired}
                            atMost={{
                                errorMessage:
                                    editIncomeDrawdownDetailsMessages.cannotBeGreaterThanMaximumIncomePaymentForRemainderOfYear,
                                value: calculateMaximumIncomePaymentForRemainderOfYear(
                                    processTypeEnum.NewBusiness,
                                    data?.CappedTransfer1,
                                    pensionBase
                                ),
                            }}
                            disabled={
                                calculateMaximumIncomePaymentForRemainderOfYear(
                                    processTypeEnum.NewBusiness,
                                    data?.CappedTransfer1,
                                    pensionBase
                                ) <= 0
                            }
                        />
                    </div>
                    {data.CappedTransfer2 && (
                        <div className="col-4">
                            <MoneyTextBoxEditor
                                data-test="currentIncome2"
                                label=""
                                value={data?.CappedTransfer2?.IncomePaymentDetails?.GrossIncomePerPayment?.Amount}
                                currency={CurrencyCode.GBP}
                                onChange={(v) =>
                                    onChange(
                                        {
                                            GrossIncomePerPayment: {
                                                Amount: v,
                                                Currency: CurrencyCode.GBP,
                                            },
                                        },
                                        data.CappedTransfer2
                                    )
                                }
                                required={
                                    editIncomeDrawdownDetailsMessages.incomePerPaymentForCurrentPensionYearRequired
                                }
                                atMost={{
                                    errorMessage:
                                        editIncomeDrawdownDetailsMessages.cannotBeGreaterThanMaximumIncomePaymentForRemainderOfYear,
                                    value: calculateMaximumIncomePaymentForRemainderOfYear(
                                        processTypeEnum.NewBusiness,
                                        data?.CappedTransfer2,
                                        pensionBase
                                    ),
                                }}
                                disabled={
                                    calculateMaximumIncomePaymentForRemainderOfYear(
                                        processTypeEnum.NewBusiness,
                                        data?.CappedTransfer2,
                                        pensionBase
                                    ) <= 0
                                }
                            />
                        </div>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">
                        {editIncomeDrawdownDetailsLabels.incomePerPaymentForFuturePensionYears}
                    </span>
                    <div className="col-4">
                        <MoneyTextBoxEditor
                            data-test="futureIncome1"
                            label=""
                            value={data.CappedTransfer1.IncomePaymentDetails.FutureYearIncome?.Amount}
                            currency={
                                data.CappedTransfer1.IncomePaymentDetails?.FutureYearIncome?.Currency ||
                                CurrencyCode.GBP
                            }
                            onChange={(v) =>
                                onChange(
                                    {
                                        FutureYearIncome: {
                                            Amount: v,
                                            Currency: CurrencyCode.GBP,
                                        },
                                    },
                                    data.CappedTransfer1
                                )
                            }
                            required={editIncomeDrawdownDetailsMessages.incomePerPaymentForFuturePensionYearsRequired}
                            atMost={{
                                errorMessage:
                                    editIncomeDrawdownDetailsMessages.cannotBeGreaterThanMaximumIncomePaymentForFutureYears,
                                value: calculateMaximumIncomePaymentForFutureYear(
                                    processTypeEnum.NewBusiness,
                                    data?.CappedTransfer1,
                                    pensionBase
                                ),
                            }}
                        />
                    </div>
                    {data.CappedTransfer2 && (
                        <div className="col-4">
                            <MoneyTextBoxEditor
                                data-test="futureIncome2"
                                label=""
                                value={data.CappedTransfer2?.IncomePaymentDetails.FutureYearIncome?.Amount}
                                currency={
                                    data.CappedTransfer2?.IncomePaymentDetails?.FutureYearIncome?.Currency ||
                                    CurrencyCode.GBP
                                }
                                onChange={(v) =>
                                    onChange(
                                        {
                                            FutureYearIncome: {
                                                Amount: v,
                                                Currency: CurrencyCode.GBP,
                                            },
                                        },
                                        data.CappedTransfer2
                                    )
                                }
                                required={
                                    editIncomeDrawdownDetailsMessages.incomePerPaymentForFuturePensionYearsRequired
                                }
                                atMost={{
                                    errorMessage:
                                        editIncomeDrawdownDetailsMessages.cannotBeGreaterThanMaximumIncomePaymentForFutureYears,
                                    value: calculateMaximumIncomePaymentForFutureYear(
                                        processTypeEnum.NewBusiness,
                                        data?.CappedTransfer2,
                                        pensionBase
                                    ),
                                }}
                            />
                        </div>
                    )}
                </div>
                <hr />
            </>
        );
    }
}

export default CappedTransferRow;
